import { useRef, useMemo, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { uploadFile } from "../../utils/upload";
import QuillResizeImage from "quill-resize-image";

const registerOnce = (key, value) => {
  if (!Quill.imports[key]) {
    Quill.register(key, value, true);
  }
};

registerOnce("modules/resize", QuillResizeImage);

const Font = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "roboto",
  "poppins",
  "lato",
  "montserrat",
  "serif",
  "monospace",
];
registerOnce("formats/font", Font);

const Size = Quill.import("formats/size");
Size.whitelist = ["small", "normal", "large", "huge"];
registerOnce("formats/size", Size);

export default function RichTextEditor({ value, onChange, height = 300 }) {
  const quillRef = useRef(null);

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        const randomId = Math.random().toString(36).substring(2, 15);

        const data = await uploadFile(file, "blog-content", `editor-${randomId}`);
        const imageUrl = data.secure_url;

        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);

        editor.insertEmbed(range.index, "image", imageUrl);
      } catch (err) {
        console.error("Editor image upload failed:", err);
      }
    };
  }, []);

  const normalizeUrl = useCallback((url) => {
  if (!url) return "";
  if (/^(https?:|mailto:|tel:|#|\/)/i.test(url)) return url;
  return `https://${url}`;
}, []);

const linkHandler = useCallback(() => {
  const quill = quillRef.current.getEditor();
  const range = quill.getSelection();
  if (!range) return;

  const value = prompt("Enter link URL");
  if (value) quill.format("link", normalizeUrl(value));
}, [normalizeUrl]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          [{ size: Size.whitelist }],

          ["bold", "italic", "underline", "strike"],
          ["blockquote", "code-block"],

          [{ color: [] }, { background: [] }],
          [{ align: [] }],

          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],

          ["link", "image", "video", "emoji"],
          ["clean"],
        ],
        handlers: { 
            image: imageHandler,
            link: linkHandler,
         },
      },

      resize: {}
      }),
  [imageHandler, linkHandler]
);

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "code-block",
    "color",
    "background",
    "align",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "emoji",
  ];

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={value}
      onChange={onChange}
      modules={modules}
      formats={formats}
      style={{ height , marginBottom: "60px" }}
    />
  );
}
